import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import merchant_services from "services/merchant_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { parseApiError } from "utils/errors/parseApiError";
import { toTerminalRecord } from "features/terminals/useTerminals";
import type { TerminalRecord } from "components/terminal-row/TerminalRow";
import type { PosByTreasury } from "utils/AxiosUtil";

export type TerminalLookupStatus =
  | "idle" // no lookup is running (disabled by the caller, or no auth yet)
  | "loading" // a request is in flight for the current serial
  | "found" // the chain returned a matching terminal
  | "not-found" // the chain answered, definitively, with no such terminal
  | "error"; // the lookup could not be completed (network/server error)

export interface UseTerminalLookupResult {
  status: TerminalLookupStatus;
  terminal: TerminalRecord | null;
  /** Re-run the lookup — used to recover from the `error` state. */
  refetch: () => void;
}

/**
 * Looks one of THIS merchant's terminals up on-chain by its full hardware serial — the fallback
 * for when it isn't among the terminals already loaded. The route is scoped by
 * requireOwnMerchantId, so another merchant's serial answers not-found, never a record.
 *
 * A miss is not a 200 with an empty body: /posBySerialForMerchant throws POS_NOT_FOUND, which
 * axios surfaces as a request error. So the not-found/error split reads the backend's code rather
 * than checking whether data came back — any other code still means "couldn't complete the
 * lookup", and must not be reported as "no such terminal".
 */
export function useTerminalLookup(
  serial: string,
  merchantId: string,
  enabled: boolean,
): UseTerminalLookupResult {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  const normalized = serial.trim();
  const active = !!user && !!merchantId && enabled && normalized.length > 0;

  const query = useQuery<PosByTreasury>({
    // networkType is in the key: a serial resolves per deployment, and staleTime would otherwise
    // serve one network's terminal for the other.
    queryKey: ["terminal-lookup", merchantId, networkType, normalized],
    queryFn: () =>
      merchant_services.lookupTerminal(merchantId, normalized, networkType),
    enabled: active,
    retry: false,
    staleTime: 60_000,
  });

  const terminal = active && query.data ? toTerminalRecord(query.data) : null;

  const isPosNotFound =
    query.isError && parseApiError(query.error, "").code === "POS_NOT_FOUND";

  let status: TerminalLookupStatus;
  if (!active) {
    status = "idle";
  } else if (query.isFetching) {
    status = "loading";
  } else if (isPosNotFound) {
    status = "not-found";
  } else if (query.isError) {
    status = "error";
  } else if (query.isSuccess) {
    status = terminal ? "found" : "not-found";
  } else {
    // Active but not yet fetching — the tick between enabling and the request actually starting.
    // Treat as loading so we never flash a stale outcome.
    status = "loading";
  }

  return { status, terminal, refetch: query.refetch };
}
